import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, BarChart3, Target, Flame, ArrowLeft } from "lucide-react";

/**
 * TGPSC Topic Heatmap Page
 * Reuses the same backend API as UPSC with exam_type=TGPSC
 * Shows importance matrix for TGPSC-specific and general subjects
 */
const TGPSCHeatmapPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("Telangana History");
  const [result, setResult] = useState(null);

  // TGPSC-specific subjects + General subjects
  const subjects = [
    // Telangana-Specific Subjects (Important for State PSC)
    "Telangana History",
    "Telangana Geography",
    "Telangana Economy",
    "Telangana Movement & State Formation",
    "Telangana Society & Culture",
    "Telangana Governance & Welfare Schemes",
    
    // General Studies (Common with UPSC but with state context)
    "Indian Polity & Constitution",
    "Indian Economy",
    "Indian Geography",
    "Indian History",
    "Environment & Ecology",
    "Science & Technology",
    "Current Affairs",
    "Governance & Public Policy"
  ];

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(
        `${API_BASE}/prelims/heatmap/${encodeURIComponent(subject)}?exam_type=TGPSC`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Generated complete heatmap for ${subject}`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to generate heatmap"
      });
    } finally {
      setLoading(false);
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case "Very High":
        return "bg-red-500";
      case "High":
        return "bg-orange-500";
      case "Medium":
        return "bg-yellow-500";
      default:
        return "bg-slate-500";
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 80) return "text-red-600";
    if (prob >= 60) return "text-orange-600";
    if (prob >= 40) return "text-yellow-600";
    return "text-slate-600";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tgpsc/prelims')} 
          className="mb-4 text-slate-400 hover:text-white"
          data-testid="back-to-prelims"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to TGPSC Prelims Hub
        </Button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Topic Heatmap</h1>
            <p className="text-sm text-slate-400">
              Importance matrix for TGPSC 2026 topics
            </p>
          </div>
          <Badge className="bg-pink-500">
            TGPSC Group-1
          </Badge>
        </div>

        {/* Info Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <BarChart3 className="w-5 h-5 text-pink-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-white mb-1">
                  Complete Heatmap Analysis for TGPSC:
                </p>
                <p className="text-sm text-slate-400">
                  View all topics with importance levels, probability scores, 
                  PYQ frequency, and Current Affairs relevance for Telangana Group-1 preparation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="subject" className="text-white">Select Subject</Label>
              <select
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 mt-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                data-testid="subject-select"
              >
                <optgroup label="Telangana-Specific">
                  {subjects.slice(0, 6).map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="General Studies">
                  {subjects.slice(6).map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700"
              data-testid="generate-heatmap-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Heatmap...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Heatmap
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-4">
            {/* Top Topic Highlight */}
            {result.top_topic && (
              <Card className="bg-gradient-to-br from-pink-500/20 to-rose-600/20 border-pink-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Flame className="w-6 h-6 text-pink-400" />
                    <h3 className="font-bold text-white text-lg">
                      #1 Most Important Topic
                    </h3>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xl font-bold text-white">
                      {result.top_topic.topic}
                    </p>
                    <div className="flex items-center gap-4">
                      <Badge className={getImportanceColor(result.top_topic.importance)}>
                        {result.top_topic.importance}
                      </Badge>
                      <div>
                        <p className="text-sm text-slate-400">Probability</p>
                        <p className={`text-2xl font-bold ${getProbabilityColor(result.top_topic.probability)}`}>
                          {result.top_topic.probability}%
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Complete Heatmap */}
            {result.heatmap && result.heatmap.length > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-pink-400" />
                  <h3 className="font-semibold text-white">
                    Complete Topic Heatmap for {subject}
                  </h3>
                </div>

                {/* Legend */}
                <Card className="bg-slate-800/30 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <span className="font-semibold text-slate-400">Importance:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-red-500"></div>
                        <span className="text-slate-400">Very High</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-orange-500"></div>
                        <span className="text-slate-400">High</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-yellow-500"></div>
                        <span className="text-slate-400">Medium</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-slate-500"></div>
                        <span className="text-slate-400">Low</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Heatmap Table */}
                <div className="space-y-2">
                  {result.heatmap.map((item, idx) => (
                    <Card
                      key={idx}
                      className={`border-l-4 ${
                        item.importance === "Very High"
                          ? "border-l-red-500 bg-red-500/10"
                          : item.importance === "High"
                          ? "border-l-orange-500 bg-orange-500/10"
                          : item.importance === "Medium"
                          ? "border-l-yellow-500 bg-yellow-500/10"
                          : "border-l-slate-500 bg-slate-800/50"
                      } border-slate-700`}
                    >
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                          {/* Rank */}
                          <div className="md:col-span-1">
                            <Badge variant="outline" className="text-lg font-bold text-white border-slate-600">
                              #{idx + 1}
                            </Badge>
                          </div>

                          {/* Topic */}
                          <div className="md:col-span-5">
                            <p className="font-semibold text-white">{item.topic}</p>
                          </div>

                          {/* Importance */}
                          <div className="md:col-span-2">
                            <p className="text-xs text-slate-400 mb-1">Importance</p>
                            <Badge className={getImportanceColor(item.importance)}>
                              {item.importance}
                            </Badge>
                          </div>

                          {/* Probability */}
                          <div className="md:col-span-2">
                            <p className="text-xs text-slate-400 mb-1">Probability</p>
                            <p className={`text-xl font-bold ${getProbabilityColor(item.probability)}`}>
                              {item.probability}%
                            </p>
                          </div>

                          {/* Additional Metrics */}
                          <div className="md:col-span-2">
                            <div className="space-y-1">
                              {item.pyq_frequency !== undefined && (
                                <p className="text-xs text-slate-400">
                                  PYQ: {item.pyq_frequency}
                                </p>
                              )}
                              {item.ca_relevance && (
                                <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                                  CA: {item.ca_relevance}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* Tips Card */}
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-amber-400 mb-2">
                  How to Use This Heatmap
                </h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>• Focus on <strong className="text-white">Very High</strong> and <strong className="text-white">High</strong> importance topics first</li>
                  <li>• Topics with high probability (80%+) are likely to appear in TGPSC 2026</li>
                  <li>• Telangana-specific subjects carry significant weightage</li>
                  <li>• Check PYQ frequency to understand historical patterns</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TGPSCHeatmapPage;
